import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { ALERT_CENTER_NS } from 'src/alerts/AlertCenter.constants';
import {
  canAcknowledge,
  withActionableCta,
} from 'src/alerts/AlertCenter.functions';
import type { tLiveAlert } from 'src/alerts/AlertCenter.types';
import { useAlertCenterContext } from 'src/providers/AlertCenterProvider/AlertCenter.hook';
import { useUserContext } from 'src/providers/UserProvider/User.context';
import { ALERT_TIER_ICON } from './AlertCenter.icons';
import { useAlertCta } from './AlertCenter.cta.hook';
import { useFocusTrap } from './AlertPanel.hook';
import {
  ALERT_DETAIL_PLAIN_HEADER_IDS,
  ALERT_DETAIL_RENDERERS,
} from './detail/AlertDetail.registry';
import AlertDetailBody from './detail/AlertDetailBody.component';
import './AlertDetailModal.styles.scss';

export type tAlertDetailModalProps = {
  /** The alert whose detail was opened — the identity anchor while open. */
  alert: tLiveAlert;
  onClose: () => void;
};

/**
 * The alert detail modal (U4/RET-1878, R12): one modal that renders any
 * alert's full explanation. Rich alerts bring a renderer from
 * `ALERT_DETAIL_RENDERERS`; everything else shows title, body and actions.
 *
 * Follows `HandoffReasonModal`, the repo's best focus handling: portal,
 * `role="dialog"` + `aria-modal`, Escape, click-outside armed on a zero
 * timeout so the opening click cannot immediately close it. The focus trap is
 * `useFocusTrap` (shipped with the panel in RET-1877); focus RESTORE is the
 * opener's job (`AlertBell`), because on close this component no longer
 * exists. MOUNTED conditionally, never hidden (KTD14).
 *
 * Liveness: the alert is re-read from `derivedAlerts` by key each render, so
 * copy values stay fresh while the episode lives — and when it resolves under
 * the user, the modal does NOT unmount: the body swaps to a resolved note and
 * the user closes it themselves (a modal that vanishes mid-read looks like a
 * crash, not a resolution). Judged against `derivedAlerts`, not `alerts`,
 * so acknowledging from the panel behind never fakes a resolution.
 */
export default function AlertDetailModal(props: tAlertDetailModalProps) {
  const { t } = useTranslation(ALERT_CENTER_NS);
  const { derivedAlerts, acknowledgeAlert } = useAlertCenterContext();
  const { currentUserRole } = useUserContext();
  const runCta = useAlertCta({ surface: 'detail' });
  const containerRef = useRef<HTMLDivElement>(null);

  /**
   * REL-2, restated by RET-1957 (U3). While a renderer's NESTED modal is open
   * (the reactivation / accept / unlock checkouts) this detail:
   *
   *  - refuses every dismissal path, as it always has. Unmounting it unmounts
   *    the nested modal, mid-charge included, bypassing that modal's own
   *    `isProcessing` guard. THIS PART IS UNCHANGED.
   *  - HANDS THE SCREEN OVER: it stops painting instead of sitting dimmed
   *    behind the checkout. One surface at a time is the rule; "visible,
   *    dimmed, behind" was the old contract and is now the thing being fixed.
   *    It stays MOUNTED because the flow's state lives in the renderer, and
   *    cancelling the nested modal must return the user to the detail exactly
   *    as they left it. The hiding is CSS-only (see the stylesheet: the nested
   *    modals are DOM descendants, so `display: none` would take them down
   *    with the host).
   *
   * Mirrored into a ref so the document-level listeners read the current
   * value.
   */
  const [isNestedModalOpen, setIsNestedModalOpen] = useState(false);
  const isNestedModalOpenRef = useRef(isNestedModalOpen);
  isNestedModalOpenRef.current = isNestedModalOpen;
  // Held in a ref so the arming effect never re-runs (and re-arms its zero
  // timeout) because a parent re-rendered with a fresh onClose identity.
  const onCloseRef = useRef(props.onClose);
  onCloseRef.current = props.onClose;
  const requestClose = useCallback(() => {
    if (isNestedModalOpenRef.current) return;
    onCloseRef.current();
  }, []);

  useFocusTrap({ containerRef, onEscape: requestClose });

  useEffect(() => {
    const onMouseDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        requestClose();
      }
    };
    // Zero-timeout arming (HandoffReasonModal): the mousedown that opened the
    // modal must not be the mousedown that closes it.
    const armId = window.setTimeout(() => {
      document.addEventListener('mousedown', onMouseDown);
    }, 0);
    return () => {
      window.clearTimeout(armId);
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [requestClose]);

  // Liveness is judged by DECLARATION id, not by the full alert key: R13b
  // keys carry a billing-period segment that can roll over while the modal is
  // open (a late-resolving subscription swaps the fallback period for the
  // real one), and a key-match would fake a resolution for an episode that is
  // still live. Selection emits at most one live alert per declaration, so
  // the id is unambiguous; the LIVE object then carries the fresh key for
  // acknowledgment.
  const liveAlert =
    derivedAlerts.find((candidate) => candidate.id === props.alert.id) ?? null;
  const isResolved = liveAlert === null;
  const alert = liveAlert ?? props.alert;
  const role = currentUserRole ?? null;

  const { copy } = withActionableCta(alert);
  // RET-1956 — a `detail` CTA's destination is THIS modal, so rendering it in
  // the footer would be a button that reopens what the user is already
  // reading. By the footer contract in `AlertDetail.registry.ts`, `detail` is
  // also exactly the set of alerts whose renderer paints its own actions, so
  // this one test does both jobs: no reopening button, and no second copy of a
  // button the body already shows.
  const ownsActions = copy.cta?.kind === 'detail';
  const footerCta = ownsActions ? null : copy.cta;
  const Icon = ALERT_TIER_ICON[alert.priority];
  // AGI-428: title and close only for the families in the set (Ariel's
  // staging review, 16-sep). The reason lives beside the set in the registry.
  const hasPlainHeader = ALERT_DETAIL_PLAIN_HEADER_IDS.has(alert.id);
  // REL-2 extended from dismissal to RESOLUTION (RET-1956). The flows a
  // renderer now hosts are the flows that RESOLVE the alert: accepting a tier
  // PATCHes the row and the episode ends while the success step is still on
  // screen. Swapping the body for the resolved note there would unmount the
  // confirmation the user just paid for, so the swap waits for the nested
  // modal to close. Every other resolution path is untouched.
  const isBodyResolved = isResolved && !isNestedModalOpen;
  const Renderer = isBodyResolved
    ? null
    : (ALERT_DETAIL_RENDERERS[alert.id] ?? null);
  /**
   * The footer's acknowledgment, suppressed on the same alerts for the same
   * reason as its CTA: those renderers already offer the way out, and theirs
   * is the one that works. `acknowledgeAlert` writes localStorage, while
   * `la-expired`'s predicate reads the SERVER-side dismissal — so next to the
   * renderer's real "Ahora no" the footer offered a second, weaker button
   * with almost the same promise. The panel row keeps its acknowledgment;
   * postponing from the glance is what that surface is for.
   */
  const isAcknowledgeable =
    !isResolved && role !== null && !ownsActions
      ? canAcknowledge(alert, role)
      : false;
  const title = t(alert.copy.titleKey, alert.copy.values ?? {});

  return createPortal(
    <div
      className={classNames('AlertDetailModal', {
        'AlertDetailModal--handedOver': isNestedModalOpen,
        'AlertDetailModal--plainHeader': hasPlainHeader,
      })}
      data-testid="alert-detail-modal"
      data-handed-over={isNestedModalOpen || undefined}
    >
      <div className="AlertDetailModal__Backdrop" aria-hidden="true" />
      <div
        ref={containerRef}
        className="AlertDetailModal__Dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="alert-detail-title"
        tabIndex={-1}
        data-priority={alert.priority}
      >
        <div className="AlertDetailModal__Header">
          {hasPlainHeader ? null : (
            <span
              className={`AlertDetailModal__IconBadge AlertDetailModal__IconBadge--${alert.priority}`}
              aria-hidden="true"
              data-testid="alert-detail-icon-badge"
            >
              <Icon
                className={`AlertDetailModal__Icon AlertDetailModal__Icon--${alert.priority}`}
              />
            </span>
          )}
          <h2 className="AlertDetailModal__Title" id="alert-detail-title">
            {title}
          </h2>
          <button
            type="button"
            className="AlertDetailModal__Close"
            aria-label={t('detail.close')}
            data-testid="alert-detail-close"
            onClick={requestClose}
          >
            <XMarkIcon className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>

        <div className="AlertDetailModal__Content">
          {isBodyResolved ? (
            <p
              className="AlertDetailModal__Resolved"
              data-testid="alert-detail-resolved"
              role="status"
            >
              <Trans t={t} i18nKey="detail.resolved" />
            </p>
          ) : Renderer ? (
            <Renderer
              alert={alert}
              onRequestClose={requestClose}
              onNestedModalChange={setIsNestedModalOpen}
            />
          ) : (
            <AlertDetailBody alert={alert} />
          )}
        </div>

        {!isResolved && (footerCta || isAcknowledgeable) ? (
          <div className="AlertDetailModal__Footer">
            {footerCta ? (
              <button
                type="button"
                className="AlertDetailModal__Cta"
                data-testid="alert-detail-cta"
                onClick={() => {
                  // Reachable by keyboard even under a nested modal's
                  // overlay — never navigate away from a charge in flight.
                  if (isNestedModalOpen) return;
                  runCta(alert);
                  // The CTA moved the user somewhere (route, tab, widget,
                  // archive modal); a detail left floating over that would
                  // just be in the way.
                  props.onClose();
                }}
              >
                {t(footerCta.labelKey)}
              </button>
            ) : null}
            {isAcknowledgeable ? (
              <button
                type="button"
                className="AlertDetailModal__Ack"
                data-testid="alert-detail-ack"
                onClick={() => {
                  if (isNestedModalOpen) return;
                  acknowledgeAlert(alert);
                  props.onClose();
                }}
              >
                {t(
                  alert.priority === 'p3'
                    ? 'panel.dismiss'
                    : 'panel.acknowledge',
                )}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
